import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, DatePicker, Drawer, Form, Input, Select, Space, Table, Tag, message } from "antd";
import dayjs from "dayjs";
import service from "../../api/FetchInterceptor";
import ShowAppointment from "./show";

const statusColors = {
    PENDING: "orange",
    CONFIRMED: "green",
    CANCELLED: "red",
    DONE: "blue",
};

const Appointments = () => {
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState({ total: 0, per_page: 15, current_page: 1 });

    const [filtersForm] = Form.useForm();

    const [showOpen, setShowOpen] = useState(false);
    const [showId, setShowId] = useState(null);

    const load = (page = 1) => {
        const f = filtersForm.getFieldsValue();
        setLoading(true);

        service({
            method: "GET",
            url: "admin/appointments",
            params: {
                page,
                status: f.status || undefined,
                customer_id: f.customer_id || undefined,
                date: f.date ? dayjs(f.date).format("YYYY-MM-DD") : undefined,
            },
        })
            .then((response) => {
                const data = response;

                const list = data.data;
                setItems(list);

                const m = data.meta ?? data;
                setMeta({
                    total: Number(m.total ?? list.length),
                    per_page: Number(m.per_page ?? 15),
                    current_page: Number(m.current_page ?? page),
                });
            })
            .catch(() => message.error("Erreur de chargement des rendez-vous"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openShow = (id) => {
        setShowId(id);
        setShowOpen(true);
    };

    const columns = useMemo(
        () => [
            {
                title: "Code",
                dataIndex: "code",
                key: "code",
                render: (v) => v ?? "-",
            },
            {
                title: "Date / Heure",
                dataIndex: "scheduled_at",
                key: "scheduled_at",
                render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "-"),
            },
            {
                title: "Cliente",
                key: "customer",
                render: (_, row) => row.customer?.fullname ?? row.customer?.name ?? "-",
            },
            {
                title: "Service",
                key: "service",
                render: (_, row) => row.service?.title ?? row.service?.name ?? "-",
            },
            {
                title: "Prix",
                dataIndex: "total_price",
                key: "total_price",
                align: "right",
                render: (v) => (v !== null && v !== undefined ? Number(v).toLocaleString() : "-"),
            },
            {
                title: "Durée",
                dataIndex: "total_duration_min",
                key: "total_duration_min",
                render: (v) => (v ? `${v} min` : "-"),
            },
            {
                title: "Statut",
                dataIndex: "status",
                key: "status",
                render: (v) => <Tag color={statusColors[v] ?? "default"}>{v ?? "—"}</Tag>,
            },
            {
                title: "Actions",
                key: "actions",
                width: 140,
                render: (_, row) => (
                    <Space>
                        <Button onClick={() => openShow(row.id)}>Voir</Button>
                    </Space>
                ),
            },
        ],
        []
    );

    return (
        <>
            <Card
                title="Rendez-vous"
                extra={
                    <Button onClick={() => load(1)} loading={loading}>
                        Rafraîchir
                    </Button>
                }
            >
                <Form
                    form={filtersForm}
                    layout="inline"
                    onFinish={() => load(1)}
                    initialValues={{ status: null, customer_id: "", date: null }}
                    style={{ marginBottom: 16 }}
                >
                    <Form.Item name="status" label="Statut">
                        <Select
                            allowClear
                            style={{ width: 180 }}
                            options={[
                                { value: "PENDING", label: "PENDING" },
                                { value: "CONFIRMED", label: "CONFIRMED" },
                                { value: "CANCELLED", label: "CANCELLED" },
                                { value: "DONE", label: "DONE" },
                            ]}
                        />
                    </Form.Item>

                    <Form.Item name="date" label="Date">
                        <DatePicker format="DD/MM/YYYY" />
                    </Form.Item>

                    <Form.Item name="customer_id" label="Customer ID">
                        <Input placeholder="uuid..." style={{ width: 260 }} />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                Filtrer
                            </Button>
                            <Button
                                onClick={() => {
                                    filtersForm.resetFields();
                                    load(1);
                                }}
                            >
                                Reset
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>

                <Table
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    dataSource={items}
                    pagination={{
                        current: meta.current_page,
                        pageSize: meta.per_page,
                        total: meta.total,
                        onChange: (p) => load(p),
                    }}
                />
            </Card>

            <ShowAppointment id={showId} open={showOpen} setOpen={setShowOpen} />
        </>
    );
};

export default Appointments;
